import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame, Canvas, extend } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';

// Extend les composants Three.js pour React Three Fiber
extend({
  Points: THREE.Points,
  PointsMaterial: THREE.PointsMaterial,
  BufferGeometry: THREE.BufferGeometry,
  BufferAttribute: THREE.BufferAttribute,
  GridHelper: THREE.GridHelper,
  PointLight: THREE.PointLight,
  AmbientLight: THREE.AmbientLight
});

const ParticleField = () => {
  const points = useRef<THREE.Points>(null!);
  const particlesCount = 3000;
  const positions = new Float32Array(particlesCount * 3);
  const speeds = new Float32Array(particlesCount);
  const sizes = new Float32Array(particlesCount);

  // Initialiser les positions, vitesses et tailles des particules
  for (let i = 0; i < particlesCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 15;
    positions[i * 3 + 1] = Math.random() * 10 - 5;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 15;
    speeds[i] = Math.random() * 0.01 + 0.005;
    sizes[i] = Math.random() * 0.03 + 0.01;
  }

  useFrame((state) => {
    if (!points.current) return;
    
    const positions = points.current.geometry.attributes.position.array as Float32Array;
    
    for (let i = 0; i < particlesCount; i++) {
      positions[i * 3 + 1] -= speeds[i];
      
      if (positions[i * 3 + 1] < -5) {
        positions[i * 3 + 1] = 5;
        positions[i * 3] = (Math.random() - 0.5) * 15;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 15;
      }
    }
    
    points.current.rotation.y += 0.0005;
    points.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particlesCount}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={particlesCount}
          array={sizes}
          itemSize={1}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.02}
        color="#ffffff"
        transparent
        opacity={0.6}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
};

const Grid = () => {
  const gridRef = useRef<THREE.GridHelper>(null!);
  
  useFrame((state) => {
    if (!gridRef.current) return;
    gridRef.current.rotation.x = Math.PI / 2;
    gridRef.current.position.y = -2;
    gridRef.current.rotation.z += 0.0002;
  });

  const size = 30;
  const divisions = 30;
  
  return (
    <gridHelper
      ref={gridRef}
      args={[size, divisions, '#ffffff', '#ffffff']}
      position={[0, 0, 0]}
      material-opacity={0.1}
      material-transparent={true}
    />
  );
};

const MovingLights = () => {
  const light1 = useRef<THREE.PointLight>(null!);
  const light2 = useRef<THREE.PointLight>(null!);
  
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();
    
    if (light1.current && light2.current) {
      light1.current.position.x = Math.sin(time * 0.2) * 5;
      light1.current.position.z = Math.cos(time * 0.2) * 5;
      light1.current.intensity = 0.5 + Math.sin(time * 0.5) * 0.1;
      
      light2.current.position.x = Math.sin(time * 0.2 + Math.PI) * 5;
      light2.current.position.z = Math.cos(time * 0.2 + Math.PI) * 5;
      light2.current.intensity = 0.5 + Math.cos(time * 0.5) * 0.1;
    }
  });

  return (
    <>
      <pointLight ref={light1} intensity={0.5} distance={15} color="#ffffff" />
      <pointLight ref={light2} intensity={0.5} distance={15} color="#ffffff" />
      <ambientLight intensity={0.1} />
    </>
  );
};

const Scene = () => {
  return (
    <>
      <MovingLights />
      <ParticleField />
      <Grid />
      <EffectComposer>
        <Bloom
          intensity={0.7}
          luminanceThreshold={0.1}
          luminanceSmoothing={0.9}
          height={300}
        />
      </EffectComposer>
    </>
  );
};

const Background: React.FC = () => {
  return (
    <div className="fixed inset-0 z-0 bg-gradient-to-b from-black to-gray-900">
      <Canvas
        camera={{ position: [0, 0, 8], fov: 60 }}
        style={{ background: 'transparent' }}
        dpr={[1, 2]}
      >
        <Scene />
      </Canvas>
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />
    </div>
  );
};

export default Background; 